import React, { useMemo } from "react";
import { BsArrowLeft } from "react-icons/bs";
import { FiCopy } from "react-icons/fi";
import { RiErrorWarningFill } from "react-icons/ri";
import CopyToClipboard from "../../components/utils/CopyToClipboard";
import { darkLogo, logo } from "../../assets/images";
import receiptWatermark from "../../assets/images/receiptBgg.png";
import { useUser } from "../../context/user-context";
import useNairaWallet from "../../hooks/useNairaWallet";

type NairaDepositModalProps = {
  setOpenDeposit: (open: boolean) => void;
};

const PROCESSING_LABEL = "Processing";

const NairaDepositModal: React.FC<NairaDepositModalProps> = ({
  setOpenDeposit,
}) => {
  const { theme, userDetails } = useUser();
  const brandLogo = theme === "light" ? darkLogo : logo;

  const { data: nairaWalletData, isLoading } = useNairaWallet();

  const wallet = nairaWalletData?.wallet;
  const firstName = userDetails?.user?.profile?.firstName?.trim() ?? "";
  const lastName = userDetails?.user?.profile?.lastName?.trim() ?? "";

  const depositDetails = useMemo(() => {
    const accountName =
      firstName && lastName ? `${firstName} ${lastName}` : PROCESSING_LABEL;

    const hasAccountDetails =
      wallet?.status === "SUCCESS" &&
      Boolean(wallet?.bankName?.trim()) &&
      Boolean(wallet?.accountNumber?.trim());

    return {
      bankName: hasAccountDetails
        ? wallet?.bankName ?? PROCESSING_LABEL
        : PROCESSING_LABEL,
      accountName,
      accountNumber: hasAccountDetails
        ? wallet?.accountNumber ?? PROCESSING_LABEL
        : PROCESSING_LABEL,
    };
  }, [
    wallet?.bankName,
    wallet?.accountNumber,
    wallet?.status,
    firstName,
    lastName,
  ]);

  const canCopyAccountNumber =
    depositDetails.accountNumber !== PROCESSING_LABEL &&
    depositDetails.accountNumber.length > 0;

  return (
    <div className="fixed inset-0 z-[130] flex items-start justify-start overflow-auto bg-[#E7EBEE] pb-16 pt-16 font-manrope dark:bg-primary_dark dark:bg-opacity-95 backdrop-blur-sm">
      <div className="relative mx-auto w-11/12 overflow-hidden rounded-xl border bg-white px-6 py-8 dark:border-[#303030] dark:bg-[#192025] mds:w-8/12 md:w-7/12 lgss:w-2/5 xxl:w-1/3">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-xl opacity-[0.8] "
          style={{
            backgroundImage: `url(${receiptWatermark})`,
            backgroundRepeat: "repeat",
            backgroundSize: "200px auto",
            backgroundPosition: "center",
          }}
        />

        <div className="relative z-10 flex min-h-[480px] flex-col">
          <div className="relative mb-10 flex items-center justify-center">
            <button
              type="button"
              onClick={() => setOpenDeposit(false)}
              className="absolute left-0"
              aria-label="Go back"
            >
              <BsArrowLeft className="text-[20px] text-black dark:text-white" />
            </button>
            <h4 className="font-sora text-[20px] font-semibold text-gray-900 dark:text-gray-100">
              Deposit Cash
            </h4>
          </div>

          <div className="flex flex-col items-center px-4 pb-2 pt-2 text-center">
            <img src={brandLogo} alt="DeeX" className="h-[38px] w-auto" />

            <p className="mt-8 max-w-[300px] px-2 text-[13px] leading-relaxed text-gray-500 dark:text-gray-400">
              Please send your cash into your special account number below
            </p>

            <div className="mt-12 flex w-full max-w-[320px] flex-col items-center">
              {isLoading ? (
                <div className="flex w-full flex-col items-center gap-3">
                  <div className="h-4 w-32 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
                  <div className="h-4 w-40 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
                  <div className="mt-3 h-[56px] w-full animate-pulse rounded-xl bg-gray-200 dark:bg-gray-700" />
                </div>
              ) : (
                <>
                  <p className="text-[13px] font-medim uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    {depositDetails.bankName}
                  </p>

                  <p className="mt-3 text-[16px] font-semibold text-gray-600 dark:text-gray-300">
                    {depositDetails.accountName}
                  </p>

                  <div className="mt-6 flex  items-center justify-between gap-4 rounded-xl bg-[#E8F3FC] px-5 py-4 dark:bg-[#1C2227]">
                    <span className="text-[18px] font-bold tracking-wide text-[#0B75C2] dark:text-[#5CC3FF]">
                      {depositDetails.accountNumber}
                    </span>
                    {canCopyAccountNumber && (
                      <CopyToClipboard text={depositDetails.accountNumber}>
                        <button
                          type="button"
                          aria-label="Copy account number"
                          className="text-[#0B75C2] dark:text-[#5CC3FF]"
                        >
                          <FiCopy className="mt-1 text-[18px]" />
                        </button>
                      </CopyToClipboard>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="mt-12 flex items-start gap-3 rounded-xl bg-[#F3F4F6] p-4 dark:bg-[#1C2227]">
            <RiErrorWarningFill className="mt-0.5 shrink-0 text-[22px] text-secondary_button" />
            <p className="text-left text-[12px] leading-relaxed text-gray-600 dark:text-gray-400">
              Do not use any crypto related description on your bank app while
              making this transfer.
            </p>
          </div>

          <button
            type="button"
            onClick={() => setOpenDeposit(false)}
            className="mt-10 h-[48px] w-full rounded-xl bg-secondary_button text-[15px] font-semibold text-white transition hover:brightness-110"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default NairaDepositModal;
